import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Copy as CopyIcon,
  Building2,
  Check,
} from "lucide-react";
import { cn } from "../../../../utils/utils";
import { getStatusInfo } from "../../utils/statusLabels";
import { buildTimeline } from "../../utils/buildTimeline";
import {
  formatRelative,
  formatTimelineStamp,
  formatDateTime,
} from "../../utils/formatRelative";

const KIND_STYLES = {
  success: { dot: "bg-emerald-500", line: "bg-emerald-200", text: "text-emerald-700" },
  info: { dot: "bg-indigo-500", line: "bg-indigo-200", text: "text-indigo-700" },
  warning: { dot: "bg-amber-500", line: "bg-amber-200", text: "text-amber-700" },
  error: { dot: "bg-red-500", line: "bg-red-200", text: "text-red-700" },
  neutral: { dot: "bg-gray-400", line: "bg-gray-200", text: "text-gray-700" },
};

/**
 * One message card. Collapsed by default, expand to see the full timeline.
 */
const MessageTimelineCard = ({ log, shopsList, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const status = getStatusInfo(log.status);
  const StatusIcon = status.icon;
  const events = buildTimeline(log);

  const initial = (log.contactName || log.zaloName || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const shopName =
    shopsList.find((s) => s.id === log.shopId)?.name || log.shopId || "—";

  const handleCopy = (e) => {
    e.stopPropagation();
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(log.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div
      className={cn(
        "bg-white rounded-xl border shadow-sm transition-all",
        expanded ? "border-indigo-200" : "border-gray-200 hover:border-gray-300",
      )}
    >
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-start gap-3"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700 inline-flex items-center justify-center text-sm font-semibold shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900 truncate max-w-[260px]">
                  {log.contactName || log.zaloName || "Khách lạ"}
                </span>
                {log.contactZaloId && (
                  <span className="text-[11px] text-gray-400 font-mono">
                    {log.contactZaloId}
                  </span>
                )}
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ring-1",
                    status.bg,
                    status.color,
                    status.ring,
                  )}
                >
                  <StatusIcon
                    size={10}
                    className={log.status === "sending" ? "animate-spin" : ""}
                  />
                  {status.label}
                </span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5 inline-flex items-center gap-1">
                <Building2 size={11} />
                <span className="truncate max-w-[200px]">{shopName}</span>
                <span>·</span>
                <span title={formatDateTime(log.createdAt)}>
                  {formatRelative(log.createdAt)}
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-1.5 line-clamp-2">
                <span className="text-gray-400">💬 </span>
                {log.botReply || (
                  <span className="italic text-gray-400">
                    (Bot chưa phản hồi)
                  </span>
                )}
              </div>
            </div>
            <span className="shrink-0 text-gray-400">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/50">
          {log.contactQuestion && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-1">
                Trigger / Câu hỏi khách
              </div>
              <div className="text-sm text-gray-700 bg-white rounded-md p-2.5 border border-gray-200 whitespace-pre-wrap">
                {log.contactQuestion}
              </div>
            </div>
          )}

          {log.botReply && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-1">
                Bot trả lời
              </div>
              <div className="text-sm text-gray-800 bg-white rounded-md p-2.5 border border-indigo-100 whitespace-pre-wrap">
                {log.botReply}
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-2">
              Timeline gửi tin
            </div>
            <Timeline events={events} />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <div className="text-[11px] text-gray-400 font-mono">
              ID: {log.id}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                title="Copy log ID"
              >
                {copied ? <Check size={11} /> : <CopyIcon size={11} />}
                {copied ? "Đã copy" : "Copy ID"}
              </button>
              {log.contactId && (
                <Link
                  to={`/?contactId=${log.contactId}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50"
                >
                  <ExternalLink size={11} />
                  Mở cuộc trò chuyện
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Timeline = ({ events }) => {
  if (events.length === 0) {
    return (
      <div className="text-xs italic text-gray-400 px-2 py-1">
        Không có dữ liệu timeline
      </div>
    );
  }
  return (
    <ol className="relative">
      {events.map((evt, idx) => {
        const Icon = evt.icon;
        const styles = KIND_STYLES[evt.kind] || KIND_STYLES.neutral;
        const isLast = idx === events.length - 1;
        return (
          <li key={evt.key} className="relative flex gap-3 pb-3 last:pb-0">
            <div className="relative flex flex-col items-center shrink-0">
              <span
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-white shadow-sm shrink-0",
                  styles.dot,
                  evt.kind === "info" && evt.key === "sending" && "animate-pulse",
                )}
              >
                <Icon size={11} />
              </span>
              {!isLast && (
                <span className={cn("flex-1 w-px mt-1", styles.line)} />
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn("text-xs font-medium", styles.text)}>
                  {evt.label}
                </span>
                {evt.ts && (
                  <span
                    className="text-[11px] text-gray-400 font-mono"
                    title={formatDateTime(evt.ts)}
                  >
                    {formatTimelineStamp(evt.ts)}
                  </span>
                )}
              </div>
              {evt.detail && (
                <div className="text-[11px] text-gray-500 mt-0.5 whitespace-pre-wrap break-words">
                  {evt.detail}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default MessageTimelineCard;
